import { useState, useEffect, useCallback, useRef } from 'react';
import { useAuthUnified } from '@/domains/billing/hooks/shared/useAuthUnified';
import { ChatMessageService } from '@/shared/services/comments/chatMessageService';
import {
  useCommentStore,
  type ChatMessage
} from '@/shared/stores/commentStore';
import { log } from '@/lib/logger';

interface PinContext {
  pageUrl: string;
  scrollY: number;
  pinId: string;
}

interface UseCommentMessagesReturn {
  messages: ChatMessage[];
  isConnected: boolean;
  isLoading: boolean;
  isSending: boolean;
  error: string | null;
  sendMessage: (text: string, mentions?: string[], pinContext?: PinContext) => Promise<void>;
  resolveConversation: () => Promise<void>;
}

interface OptimisticMessage extends ChatMessage {
  isOptimistic: true;
  optimisticId: string;
}

/**
 * Pure Firestore-based chat hook for comment modals.
 *
 * Callers are responsible for computing channelId and entryId before
 * passing them in. For spend rows, use getChannelIdFromEntry / getEntryIdFromEntry
 * from billing/utils/channelId. For other domains, use the appropriate utility.
 *
 * Lifecycle:
 * - When isOpen becomes true: subscribes to Firestore messages
 * - When isOpen becomes false: unsubscribes, clears local state
 */
export function useCommentMessages(
  channelId: string,
  entryId: string,
  workflowId: string,
  isOpen: boolean
): UseCommentMessagesReturn {
  const { effectiveUser } = useAuthUnified();

  const incrementEntryCount = useCommentStore(state => state.incrementEntryCount);
  const resetEntryCount = useCommentStore(state => state.resetEntryCount);
  const setMessages = useCommentStore(state => state.setMessages);
  const clearMessages = useCommentStore(state => state.clearMessages);

  const [messages, setLocalMessages] = useState<ChatMessage[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [isConnected, setIsConnected] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const optimisticMessagesRef = useRef<Map<string, OptimisticMessage>>(new Map());
  const unsubscribeRef = useRef<(() => void) | null>(null);
  const subscribedChannelRef = useRef<string | null>(null);

  useEffect(() => {
    if (!isOpen || !channelId) {
      return;
    }

    if (subscribedChannelRef.current === channelId && unsubscribeRef.current) {
      return;
    }

    let cancelled = false;

    const setupSubscription = async () => {
      setIsLoading(true);
      setError(null);

      try {
        await ChatMessageService.ensureChannelExists(
          channelId,
          workflowId,
          entryId,
          { workflowName: '', entryDescription: '' }
        );

        if (cancelled) return;

        const unsubscribe = ChatMessageService.subscribeToMessages(
          channelId,
          (firestoreMessages) => {
            const optimisticMap = optimisticMessagesRef.current;
            const confirmedIds = new Set<string>();

            for (const msg of firestoreMessages) {
              for (const [optId, optMsg] of optimisticMap) {
                if (optMsg.text === msg.text && optMsg.clientId === msg.clientId) {
                  confirmedIds.add(optId);
                }
              }
            }

            for (const id of confirmedIds) {
              optimisticMap.delete(id);
            }

            const optimisticArray = Array.from(optimisticMap.values());
            const combined = [...firestoreMessages, ...optimisticArray];
            combined.sort((a, b) => a.createdAt.getTime() - b.createdAt.getTime());

            setLocalMessages(combined);
            setMessages(channelId, combined);
            setIsLoading(false);
            setIsConnected(true);
          },
          50,
          false
        );

        unsubscribeRef.current = unsubscribe;
        subscribedChannelRef.current = channelId;

        log.debug('[COMMENT-MESSAGES] Subscription established', { channelId });

      } catch (err) {
        if (cancelled) return;
        const errorMessage = err instanceof Error ? err.message : 'Failed to subscribe to messages';
        setError(errorMessage);
        setIsLoading(false);
        log.error('[COMMENT-MESSAGES] Failed to setup subscription:', err);
      }
    };

    setupSubscription();

    return () => {
      cancelled = true;
      if (unsubscribeRef.current) {
        unsubscribeRef.current();
        unsubscribeRef.current = null;
      }
      subscribedChannelRef.current = null;
      setIsConnected(false);
      setLocalMessages([]);
      setError(null);
      optimisticMessagesRef.current.clear();
      if (channelId) {
        clearMessages(channelId);
      }
    };
  }, [isOpen, channelId, entryId, workflowId, setMessages, clearMessages]);

  const sendMessage = useCallback(async (
    text: string,
    mentions: string[] = [],
    pinContext?: PinContext
  ): Promise<void> => {
    if (!isOpen || !channelId) {
      throw new Error('Chat not connected');
    }

    const trimmedText = text.trim();
    if (!trimmedText) {
      return;
    }

    const clientId = effectiveUser?.employee_id?.toString() || effectiveUser?.uid || 'unknown';
    const optimisticId = `optimistic-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`;

    const optimisticMessage: OptimisticMessage = {
      id: optimisticId,
      text: trimmedText,
      createdAt: new Date(),
      clientId,
      metadata: {
        userName: effectiveUser?.name || 'Unknown User',
        userRole: effectiveUser?.role,
        userAvatar: effectiveUser?.avatar,
        mentions,
        timestamp: new Date().toISOString(),
      },
      isOptimistic: true,
      optimisticId,
    };

    try {
      setError(null);
      setIsSending(true);

      optimisticMessagesRef.current.set(optimisticId, optimisticMessage);
      setLocalMessages(prev => [...prev, optimisticMessage]);

      const messageToSave: ChatMessage = {
        id: optimisticId,
        text: trimmedText,
        createdAt: new Date(),
        clientId,
        metadata: {
          userName: effectiveUser?.name || 'Unknown User',
          userRole: effectiveUser?.role,
          userAvatar: effectiveUser?.avatar,
          mentions,
          timestamp: new Date().toISOString(),
        },
      };

      await ChatMessageService.saveMessage(
        channelId,
        workflowId,
        entryId,
        messageToSave,
        undefined,
        undefined,
        pinContext
      );

      incrementEntryCount(entryId);

    } catch (err) {
      optimisticMessagesRef.current.delete(optimisticId);
      setLocalMessages(prev => prev.filter(m => m.id !== optimisticId));

      const errorMessage = err instanceof Error ? err.message : 'Failed to send message';
      setError(errorMessage);
      log.error('[COMMENT-MESSAGES] Failed to send message:', err);
      throw err;
    } finally {
      setIsSending(false);
    }
  }, [isOpen, channelId, entryId, workflowId, effectiveUser, incrementEntryCount]);

  const resolveConversation = useCallback(async (): Promise<void> => {
    if (!channelId) {
      throw new Error('Chat not initialized');
    }

    const resolverUserId = effectiveUser?.employee_id?.toString() || effectiveUser?.uid || 'unknown';

    try {
      setError(null);

      await ChatMessageService.markMessagesResolved(channelId, resolverUserId);
      await ChatMessageService.updateChannelActiveCount(channelId, 0);
      resetEntryCount(entryId);

      setLocalMessages([]);
      optimisticMessagesRef.current.clear();
      clearMessages(channelId);

      if (unsubscribeRef.current) {
        unsubscribeRef.current();
        unsubscribeRef.current = null;
      }
      subscribedChannelRef.current = null;
      setIsConnected(false);

    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to resolve conversation';
      setError(errorMessage);
      log.error('[COMMENT-MESSAGES] Failed to resolve conversation:', err);
      throw err;
    }
  }, [channelId, entryId, effectiveUser, resetEntryCount, clearMessages]);

  return {
    messages,
    isConnected,
    isLoading,
    isSending,
    error,
    sendMessage,
    resolveConversation,
  };
}

